// src/hooks/useTheme.ts -- Slice J / Phase 2.
//
// Reads the user's theme preference from /api/me and publishes the five
// theme tokens as CSS custom properties on document.documentElement so
// components reading var(--c-accent, ...) etc. (see src/theme.ts) pick up
// the new values on the next render.
//
// Mounted once at the top of the authed app shell (App.tsx). Default
// theme is "warm-gold" -- the current Info Hub palette, so existing users
// see no visual change until they switch.

import { useEffect } from "react";
import { useCurrentUser } from "../auth";
import { DEFAULT_THEME, THEMES, THEME_CSS_VARS, getTheme } from "../lib/themes";
import type { ThemeKey } from "../lib/themes";

export function useTheme(): void {
  const { me } = useCurrentUser();
  const theme: ThemeKey = (me?.preferences?.theme as ThemeKey | undefined) ?? DEFAULT_THEME;

  useEffect(() => {
    if (typeof document === "undefined") return;
    const tokens = getTheme(theme);
    const root = document.documentElement;
    (Object.keys(THEME_CSS_VARS) as Array<keyof typeof THEME_CSS_VARS>).forEach((tokenKey) => {
      const cssVar = THEME_CSS_VARS[tokenKey];
      root.style.setProperty(cssVar, tokens[tokenKey]);
    });
  }, [theme]);
}

// Re-export so callers can `import { THEMES, ThemeKey } from "../hooks/useTheme"`
// without a second import if they prefer. Optional convenience.
export { THEMES };
export type { ThemeKey };
